/* ============================================================================
   Romina — live inventory status (product page)
   ----------------------------------------------------------------------------
   Single source of truth = the same CSV the retailer inventory portal uses
   (settings.csv_url). Fetches it client-side, builds a SKU -> stock map, and
   exposes window.RominaInventory.getQuickShip(sku). romina-product.js consults
   this to set the product-page ETA, so the merchant only updates the sheet
   (no manual product_status metafield).

   Config + trigger come from the [data-romina-delivery] host (rendered by
   romina-delivery-estimate.liquid):
     data-inv-csv  -> CSV URL (settings.csv_url)
     data-inv-min  -> minimum quantity threshold (settings.minimum_quantity)

   Status rule (mirrors the portal's getStatus): quick-ship when
   on_hand + in_transit > minQty. Unknown SKU -> getQuickShip returns null so
   the caller falls back to the product_status metafield (no regression).

   CSV column order (by index, matches the portal): 0 SKU, 1 Description,
   then either legacy 4-col (2 On Hand, 3 In-Transit) or the Aug 2026 export
   (2+3 On Hand summed, 4+5 In-Transit summed).
   ============================================================================ */
(function () {
  'use strict';

  var host = document.querySelector('[data-romina-delivery]');
  if (!host) return;

  var csvUrl = host.getAttribute('data-inv-csv') || '';
  var minQty = parseInt(host.getAttribute('data-inv-min'), 10);
  if (isNaN(minQty)) minQty = 5;
  if (!csvUrl) return; // no source configured -> metafield fallback stays

  var TTL = 60 * 1000; // short cache: near-instant freshness, fewer refetches
  var CACHE_KEY = 'romina:inv:' + csvUrl;

  var map = {}; // exact SKU (upper) -> { onHand, transit }
  var coreMap = {}; // base+finish SKU (e.g. 17501HVS) -> complete-kit stock

  // Fishbowl lists kit boxes as 17501HVS01 / 17501HVS02; Shopify variants
  // are 17501HVS. Strip a trailing box number after a letter so finishes
  // match without collapsing different colors onto one row.
  function coreSku(sku) {
    return collapseNySameFinish(String(sku || '').toUpperCase().trim().replace(/([A-Z])0[1-9]$/, '$1'));
  }

  // New York 11xxx two-tone SKUs: Shopify repeats the finish when both
  // sides match (11008APAP = Albero Puro / Albero Puro). Fishbowl stores
  // that as a single finish (11008AP). Mixed finishes (11008APSW) stay as-is.
  function collapseNySameFinish(sku) {
    var m = String(sku || '').toUpperCase().match(/^([A-Z]*11\d+)([A-Z]+)$/);
    if (!m) return sku;
    var rest = m[2];
    if (rest.length < 4 || rest.length % 2 !== 0) return sku;
    var half = rest.length / 2;
    var a = rest.slice(0, half);
    var b = rest.slice(half);
    if (a && a === b) return m[1] + a;
    return sku;
  }

  function colQty(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }

  // New export: SKU, Description, On Hand, On Hand, In Transit, In Transit
  // On hand = C+D, in transit = E+F. Legacy 4-col sheets still use C / D.
  function rowStock(r) {
    var c = colQty(r[2]);
    var d = colQty(r[3]);
    var e = colQty(r[4]);
    var f = colQty(r[5]);
    if (r.length >= 6) return { onHand: c + d, transit: e + f };
    return { onHand: c, transit: d };
  }

  // Minimal CSV parser — handles quoted fields containing commas / quotes,
  // so a description with a comma can't shift the stock columns.
  function parseCSV(text) {
    var rows = [], row = [], field = '', inQ = false, c;
    for (var i = 0; i < text.length; i++) {
      c = text[i];
      if (inQ) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; }
          else inQ = false;
        } else { field += c; }
      } else if (c === '"') {
        inQ = true;
      } else if (c === ',') {
        row.push(field); field = '';
      } else if (c === '\n') {
        row.push(field); rows.push(row); row = []; field = '';
      } else if (c !== '\r') {
        field += c;
      }
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    return rows;
  }

  function ingest(text) {
    var rows = parseCSV(text);
    if (rows.length) rows.shift(); // drop header row
    var groups = {};
    rows.forEach(function (r) {
      var sku = (r[0] || '').trim();
      if (!sku || /^(sku|partnumber|partdescription|part)$/i.test(sku)) return;
      var rec = rowStock(r);
      var up = sku.toUpperCase();
      if (map[up]) return; // sheet 1 currently repeats each SKU; keep first
      map[up] = rec;
      var core = coreSku(up);
      if (!groups[core]) groups[core] = [];
      groups[core].push(rec);
    });

    Object.keys(groups).forEach(function (core) {
      var recs = groups[core];
      var onHand = recs[0].onHand;
      var transit = recs[0].transit;
      // Multiple box rows = one kit. Complete kits = min across boxes
      // (do not add 01+02 — that would count parts, not sellable cribs).
      for (var i = 1; i < recs.length; i++) {
        if (recs[i].onHand < onHand) onHand = recs[i].onHand;
        if (recs[i].transit < transit) transit = recs[i].transit;
      }
      coreMap[core] = { onHand: onHand, transit: transit };
    });
  }

  // Match the Shopify variant SKU (17501HVS) to Fishbowl kit rows
  // (17501HVS01 / 17501HVS02). Never fall back to digits-only — that
  // would give every Dakota finish the same ETA.
  function lookup(sku) {
    if (!sku) return null;
    var up = String(sku).toUpperCase().trim();
    if (map[up]) return map[up];
    var core = coreSku(up);
    if (coreMap[core]) return coreMap[core];
    if (map[core]) return map[core];
    return null;
  }

  function quickShip(sku) {
    var rec = lookup(sku);
    if (!rec) return null; // unknown -> caller falls back to metafield
    return (rec.onHand + rec.transit) > minQty;
  }

  window.RominaInventory = {
    getQuickShip: quickShip,
    getRecord: lookup,
    loaded: false,
    ready: null
  };

  function done() {
    window.RominaInventory.loaded = true;
    document.dispatchEvent(new CustomEvent('romina:inventory:ready'));
  }

  // Serve from a short-lived sessionStorage cache when possible.
  try {
    var cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (cached && typeof cached.csv === 'string' && (Date.now() - cached.t) < TTL) {
      ingest(cached.csv);
      window.RominaInventory.ready = Promise.resolve();
      done();
      return;
    }
  } catch (_) { /* ignore cache errors */ }

  window.RominaInventory.ready = fetch(csvUrl)
    .then(function (r) { return r.ok ? r.text() : ''; })
    .then(function (text) {
      if (!text) return;
      ingest(text);
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), csv: text }));
      } catch (_) { /* quota / private mode — fine */ }
      done();
    })
    .catch(function (e) { console.warn('[RominaInventory] CSV load failed', e); });
})();
